import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled, { css } from 'styled-components';

import { useStore } from 'models/stores';
import { Menu, MenuHeader, MenuItem } from 'shared/modules/menu';
import FR from 'shared/static/images/ic_flag_fr.svg';
import EN from 'shared/static/images/ic_flag_gb.svg';
import IS from 'shared/static/images/ic_flag_is.svg';
import NO from 'shared/static/images/ic_flag_no.svg';
import PL from 'shared/static/images/ic_flag_pl.svg';
import SV from 'shared/static/images/ic_flag_sv.svg';

const languageFlagMap = {
  is: {
    imgSrc: IS,
    altText: 'icelandic',
  },
  en: {
    imgSrc: EN,
    altText: 'english',
  },
  pl: {
    imgSrc: PL,
    altText: 'polish',
  },
  no: {
    imgSrc: NO,
    altText: 'norwegian',
  },
  sv: {
    imgSrc: SV,
    altText: 'swedish',
  },
  fr: {
    imgSrc: FR,
    altText: 'french',
  },
};

export default observer(() => {
  const [el, setEl] = useState(null);
  const stuff = useRef(null);
  const {
    uiStore: { language, t },
    companyStore: { current },
  } = useStore();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const elem = document.getElementById('language-flags');
    setEl(elem);
  }, []);

  if (!el || !current) return null;

  const languages = current.languages.split(',');

  if (languages.length <= 1 && languages.includes(language)) return null;

  const newLoc = (lang) => {
    let url = pathname.split('/');
    url.splice(2, 1, lang);
    return url.join('/') + search;
  };

  const dom = (
    <Container ref={stuff}>
      <Menu
        menuTriggerHoverOff
        menuTriggerStyle={{ opacity: 1 }}
        placement="left"
        trigger={({ isOpen }) => {
          const { imgSrc, altText } = languageFlagMap[language];
          return (
            <Flag open={isOpen}>
              <img src={imgSrc} alt={altText} />
              <Arrow open={isOpen} />
            </Flag>
          );
        }}
      >
        <MenuHeader text={t('languageSwitcher_title')} />
        {languages.map((lang) => (
          <MenuItem
            key={lang}
            onClick={() => {
              navigate(newLoc(lang));
            }}
            children={t(lang)}
          />
        ))}
      </Menu>
    </Container>
  );

  return createPortal(dom, el);
});

const Container = styled.div`
  opacity: 1;
  img {
    :hover {
      opacity: 0.8;
      cursor: pointer;
    }
  }
`;

const Flag = styled.div`
  position: relative;
  height: 32px;
  width: 32px;
  border-radius: 50%;

  img {
    transition: opacity 200ms;
  }

  :hover {
    svg {
      opacity: 1;
    }
    img {
      opacity: 0.2;
    }
    cursor: pointer;
  }

  ${(p) =>
    p.open &&
    css`
      svg {
        opacity: 1;
      }
      img {
        opacity: 0.2;
      }
    `};
`;

const DropdownArrow = styled.div`
  color: ${(p) => p.theme.white};
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: #3c3c64;
  opacity: 0;
  transition: opacity 200ms;

  svg {
    transition: opacity 200ms;
  }

  :hover {
    opacity: 0.5;
    svg {
      opacity: 1;
    }
  }

  ${(p) =>
    p.open &&
    css`
      opacity: 0.5;
      svg {
        opacity: 1;
      }
    `};
`;

const Arrow = ({ open }) => (
  <DropdownArrow open={open}>
    <svg
      width="10"
      height="6"
      viewBox="0 0 10 6"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5 6L10 0H0L5 6Z" />
    </svg>
  </DropdownArrow>
);
